import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { AuthLoginResponse, AuthUser } from "@redipos/contracts";
import { getToken, setToken } from "../../shared/http.js";
import { auth } from "./authApi.js";

interface SessionValue {
  user: AuthUser | null;
  loading: boolean;
  login: (data: AuthLoginResponse) => void;
  logout: () => void;
  /**
   * Gate de acceso por módulo interno (namespace "admin:<modulo>").
   * Hoy el backend todavía no emite permisos admin granulares (ver
   * docs/analisis/consola-admin-rediseno.md §5/§7 fase 8) — mientras tanto
   * `superadmin` pasa todo y cualquier otro rol no pasa nada. El día que el
   * backend empiece a mandar `permisos["admin:<modulo>"]`, esta función ya
   * lo respeta sin tocar un solo componente de página.
   */
  can: (moduleKey: string) => boolean;
}

const SessionCtx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!getToken()) {
      setLoading(false);
      return;
    }
    auth
      .me()
      .then(setUser)
      .catch(() => setUser(null))
      .finally(() => setLoading(false));
  }, []);

  const login = (data: AuthLoginResponse) => {
    setToken(data.token);
    setUser(data.user);
  };

  const logout = () => {
    setToken(null);
    setUser(null);
  };

  const can = (moduleKey: string) => {
    if (!user) return false;
    if (user.rol === "superadmin") return true;
    return user.permisos?.[`admin:${moduleKey}`] === true;
  };

  const value = useMemo(() => ({ user, loading, login, logout, can }), [user, loading]);

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionCtx);
  if (!ctx) throw new Error("useSession debe usarse dentro de <SessionProvider>");
  return ctx;
}
